/**
 * AdminHomepageVersion — TREATX-LANDING-V3-PHASE-A
 * -----------------------------------------------
 * Website Content → Homepage Version Control.
 *
 * Reuses the existing admin shell, the existing API client and the existing
 * RBAC route guard. The server is the authority: `PUT /api/admin/homepage-version`
 * refuses anyone who is not a TreatX super admin and refuses an unconfirmed
 * request, so this screen cannot grant itself permission.
 *
 * Publication is GLOBAL (both the GLOBAL and TR surfaces resolve the same
 * published version); the screen states that explicitly rather than implying a
 * per-surface model that does not exist.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ExternalLink, RotateCcw } from 'lucide-react';
import api from '../../utils/api';
import { apiErrorMessage } from '../../utils/apiError';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';

// TREATX-WEBSITE-MGMT-01 Stage 2 — release readiness is reported in three
// honest categories. Order matters: what stops a release comes first.
const ISSUE_GROUPS = [
  {
    id: 'blockers',
    label: 'Publication blockers',
    tone: 'text-red-700',
    note: 'must be cleared before this version can go live',
  },
  {
    id: 'warnings',
    label: 'Open warnings',
    tone: 'text-amber-700',
    note: 'need a decision, but do not block publication',
  },
  {
    id: 'historical',
    label: 'Resolved / historical',
    tone: 'text-slate-500',
    note: 'already closed — kept for the audit trail',
  },
];


const SEVERITY_STYLES = {
  critical: 'text-red-700 border-red-300',
  high: 'text-orange-700 border-orange-300',
  medium: 'text-amber-700 border-amber-300',
  low: 'text-slate-600 border-slate-300',
};


const AdminHomepageVersion = ({ embedded = false }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { admin } = useTreatXAdminAuth();
  const isSuperAdmin = admin?.role === 'treatx_super_admin';
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Release readiness is a SEPARATE authorization from publishing.
  const [readiness, setReadiness] = useState(null);   // { version, ready }
  const [ackText, setAckText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/homepage-version');
      setState(data);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not read the homepage publication state'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Same convention as the Maintenance surface: the server 403 remains the
  // authoritative protection; this only keeps a non-super-admin out of a screen
  // whose single action they cannot perform.
  useEffect(() => {
    if (admin && !isSuperAdmin) navigate('/treatx-admin/dashboard', { replace: true });
  }, [admin, isSuperAdmin, navigate]);

  if (admin && !isSuperAdmin) return null;

  const publish = async (version) => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/homepage-version', {
        version,
        confirm: true,
        reason: 'Published from Homepage Version Control',
      });
      // The change succeeded, but if its final audit record could not be
      // written the outcome is UNCERTAIN — it must not look like an ordinary
      // success, and it must not invite a blind retry.
      toast(data.audit_commit_recorded === false ? {
        title: `${data.published_version} published — audit record incomplete`,
        description: 'The homepage was changed but the final audit record could not '
          + 'be written. Do not retry blindly: reload and verify before acting further.',
        variant: 'destructive',
      } : {
        title: 'Homepage published',
        description: `The public homepage now renders ${data.published_version}.`,
      });
      setPending(null);
      await load();
    } catch (err) {
      toast({
        title: 'Not published',
        description: apiErrorMessage(err, 'The homepage version was not changed'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const published = state?.published_version || 'V1';

  // ── Release readiness ────────────────────────────────────────────────
  // It only makes a version eligible. The server additionally requires the
  // version typed back, so no unrelated click can mark an incomplete page ready.
  const applyReadiness = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/homepage-version/release-readiness', {
        version: readiness.version,
        ready: readiness.ready,
        confirm: true,
        acknowledge_version: ackText.trim().toUpperCase(),
        reason: 'Release readiness changed from Homepage Version Control',
      });
      toast(data.audit_commit_recorded === false ? {
        title: 'Readiness changed — audit record incomplete',
        description: data.note,
        variant: 'destructive',
      } : {
        title: readiness.ready ? 'Marked release-ready' : 'Readiness withdrawn',
        description: data.note,
      });
      setReadiness(null);
      setAckText('');
      await load();
    } catch (err) {
      toast({
        title: 'Readiness unchanged',
        description: apiErrorMessage(err, 'The release readiness was not changed'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-slate-50 p-6'} data-testid="admin-homepage-version">
      <div className={embedded ? 'space-y-6' : 'max-w-4xl mx-auto space-y-6'}>
        {!embedded && (
          <div>
            <p className="text-xs font-semibold tracking-[0.18em] text-slate-500 uppercase">
              Website Content
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">
              Homepage Version Control
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Chooses which landing experience the public address <code>/</code> renders.
              Preview links never change what visitors see. Switching the version affects
              the public homepage only — no dashboard, workflow or data is touched.
            </p>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
               data-testid="admin-homepage-version-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Currently published</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <Badge className="bg-emerald-100 text-emerald-800"
                       data-testid="admin-homepage-published-badge">
                  {published}
                </Badge>
                <span className="text-sm text-slate-600">
                  {state?.is_default
                    ? 'No version has been published yet — the safe default V1 is live.'
                    : `Published ${(state?.published_at || '').slice(0, 19).replace('T', ' ')} UTC`}
                </span>
                <span className="text-xs text-slate-500">
                  Applies to both the international and Turkish surfaces.
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 sm:grid-cols-3">
          {(state?.versions || []).map((v) => {
            const isLive = v.version === published;
            // The server decides publishability (archived V2, unreleased V3);
            // this is the matching presentation, never the security mechanism.
            const blocked = v.publishable === false;
            return (
              <Card key={v.version}
                    className={isLive ? 'border-emerald-300 shadow-sm'
                                      : blocked ? 'border-slate-200 bg-slate-50/60' : ''}
                    data-testid={`homepage-version-card-${v.version.toLowerCase()}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {v.display_name}
                    {isLive && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                    {blocked && (
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wide"
                             data-testid={`homepage-version-blocked-${v.version.toLowerCase()}`}>
                        {v.block_code === 'VERSION_ARCHIVED' ? 'Archived' : 'Under construction'}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-slate-600 leading-relaxed">{v.note}</p>
                  <Link to={v.preview_path}
                        className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 hover:text-slate-900"
                        data-testid={`homepage-version-preview-${v.version.toLowerCase()}`}>
                    Preview {v.preview_path} <ExternalLink className="h-3 w-3" />
                  </Link>
                  <Button
                    variant={isLive ? 'outline' : 'default'}
                    className="w-full"
                    disabled={isLive || saving || !v.available || blocked}
                    onClick={() => setPending(v)}
                    data-testid={`homepage-version-publish-${v.version.toLowerCase()}`}
                  >
                    {isLive ? 'Published'
                            : blocked ? 'Cannot be published'
                                      : `Publish ${v.version}`}
                  </Button>
                  {blocked && v.block_reason && (
                    <p className="text-[11px] text-amber-700 leading-relaxed"
                       data-testid={`homepage-version-block-reason-${v.version.toLowerCase()}`}>
                      {v.block_reason}
                    </p>
                  )}
                  {/* TREATX-LANDING-VERSION-KNOWN-ISSUES-01 — the owner sees why a
                      version is archived and what is still unresolved before deciding
                      to publish or reactivate it. Content comes from the version
                      authority, never hardcoded in this component. */}
                  {v.archived && (
                    <div className="rounded-md border border-red-300 bg-red-50 p-2"
                         data-testid={`homepage-version-archived-warning-${v.version.toLowerCase()}`}>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-red-800">
                        Archived — not recommended for publication
                      </p>
                      <p className="mt-1 text-[11px] leading-relaxed text-red-700">
                        This version is retained for historical reference. Known functional and
                        visual inconsistencies have not been remediated. Reactivation requires
                        explicit owner review and authorization.
                      </p>
                    </div>
                  )}
                  {Array.isArray(v.known_issues) && v.known_issues.length > 0 && (
                    <div className="space-y-3 rounded-md border border-slate-200 bg-white p-2"
                         data-testid={`homepage-version-known-issues-${v.version.toLowerCase()}`}>
                      {/* Stage 2 — three honest categories instead of one
                          undifferentiated list. Blockers first, then open
                          warnings, then resolved history. Nothing is hidden:
                          the historical group stays expandable. */}
                      {ISSUE_GROUPS.map(({ id, label, tone, note }) => {
                        const list = (v.issue_groups || {})[id] || [];
                        if (list.length === 0) return null;
                        return (
                          <div key={id} data-testid={`homepage-version-issues-${id}-${v.version.toLowerCase()}`}>
                            <p className={`text-[11px] font-semibold ${tone}`}>
                              {label} ({list.length})
                              <span className="ml-1 font-normal text-slate-500">· {note}</span>
                            </p>
                            {list.map((issue) => (
                              <div key={issue.id}
                                   className={`mt-1.5 border-t border-slate-100 pt-1.5 first:border-0 ${
                                     id === 'historical' ? 'opacity-70' : ''}`}
                                   data-testid={`homepage-version-issue-${issue.id}`}>
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <Badge variant="outline"
                                         className={`text-[10px] uppercase ${SEVERITY_STYLES[issue.severity] || 'text-slate-600'}`}>
                                    {issue.severity}
                                  </Badge>
                                  <span className="text-[11px] font-medium text-slate-800">{issue.title}</span>
                                  {issue.blocks_publication && (
                                    <Badge variant="outline" className="text-[10px] uppercase text-red-700 border-red-300">
                                      Blocks publication
                                    </Badge>
                                  )}
                                  {id === 'historical' && (
                                    <Badge variant="outline" className="text-[10px] uppercase text-slate-500 border-slate-300">
                                      Resolved
                                    </Badge>
                                  )}
                                </div>
                                <p className="mt-0.5 text-[11px] leading-relaxed text-slate-600">{issue.detail}</p>
                                <p className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-500">
                                  {issue.affects} · {issue.status}
                                </p>
                                {issue.owner_decision && (
                                  <p className="mt-0.5 text-[11px] italic leading-relaxed text-slate-600">
                                    Owner decision: {issue.owner_decision}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Readiness is offered only where it is meaningful: V1 is
                      always publishable and V2 is archived in code. */}
                  {v.block_code !== 'VERSION_ARCHIVED' && v.version !== 'V1' && (
                    <button
                      type="button"
                      onClick={() => { setReadiness({ version: v.version, ready: blocked }); setAckText(''); }}
                      disabled={saving}
                      className="text-[11px] font-medium text-slate-600 underline underline-offset-2 hover:text-slate-900 disabled:opacity-50"
                      data-testid={`homepage-version-readiness-${v.version.toLowerCase()}`}
                    >
                      {blocked ? `Mark ${v.version} release-ready…`
                               : `Withdraw ${v.version} release readiness…`}
                    </button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {published !== 'V1' && (
          <Button
            variant="outline"
            onClick={() => setPending({ version: 'V1', display_name: 'V1 — Current' })}
            disabled={saving}
            data-testid="homepage-version-rollback-v1"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Roll back to V1
          </Button>
        )}

        {pending && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4"
               data-testid="homepage-version-confirm-dialog">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <h2 className="text-lg font-semibold text-slate-900">
                Publish {pending.display_name}?
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                Every visitor to the public homepage will see this version
                immediately. Dashboards, logins and all other pages are unaffected,
                and you can switch back at any time.
              </p>
              {pending.archived && (
                <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3"
                     data-testid="homepage-version-archived-reactivation-warning">
                  <p className="text-xs font-semibold uppercase tracking-wide text-red-800">
                    Archived — not recommended for publication
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-red-700">
                    This version is retained for historical reference. Known functional and visual
                    inconsistencies have not been remediated. Reactivation requires explicit owner
                    review and authorization.
                  </p>
                </div>
              )}
              {Array.isArray(pending.known_issues)
                && pending.known_issues.some((i) => i.blocks_publication) && (
                <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-amber-800"
                    data-testid="homepage-version-confirm-blocking-issues">
                  {pending.known_issues.filter((i) => i.blocks_publication).map((i) => (
                    <li key={i.id}>{i.title}</li>
                  ))}
                </ul>
              )}
              <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button variant="ghost" onClick={() => setPending(null)} disabled={saving}
                        data-testid="homepage-version-confirm-cancel">
                  Cancel
                </Button>
                <Button onClick={() => publish(pending.version)} disabled={saving}
                        data-testid="homepage-version-confirm-publish">
                  {saving ? 'Publishing…' : 'Yes, publish'}
                </Button>
              </div>
            </div>
          </div>
        )}
        {readiness && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4"
               data-testid="homepage-version-readiness-dialog">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <h2 className="text-lg font-semibold text-slate-900">
                {readiness.ready ? `Mark ${readiness.version} release-ready?`
                                 : `Withdraw ${readiness.version} release readiness?`}
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                {readiness.ready
                  ? `This only makes ${readiness.version} eligible to be published. It does NOT change the public homepage — you still have to publish it deliberately.`
                  : `${readiness.version} will no longer be eligible for publication. The published homepage is not changed.`}
              </p>
              <label className="mt-4 block text-xs font-medium text-slate-700">
                Type {readiness.version} to confirm
                <input
                  value={ackText}
                  onChange={(e) => setAckText(e.target.value)}
                  className="mt-1 h-9 w-full rounded-md border border-slate-300 px-2 text-sm uppercase"
                  data-testid="homepage-version-readiness-ack"
                />
              </label>
              <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button variant="ghost" onClick={() => { setReadiness(null); setAckText(''); }}
                        disabled={saving} data-testid="homepage-version-readiness-cancel">
                  Cancel
                </Button>
                <Button onClick={applyReadiness}
                        disabled={saving || ackText.trim().toUpperCase() !== readiness.version}
                        data-testid="homepage-version-readiness-confirm">
                  {saving ? 'Saving…' : 'Confirm'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminHomepageVersion;
